import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  type FormInstance,
} from "antd";

interface DiscountConfigurationProps {
  disabled?: boolean;
  courseOptions?: { label: string; value: string }[];
  form: FormInstance<any>;
  onGenerateCode?: () => void;
  isGenerating?: boolean;
}

const DiscountConfiguration = ({
  disabled,
  courseOptions = [],
  form,
  isGenerating,
  onGenerateCode,
}: DiscountConfigurationProps) => {
  const offerType = Form.useWatch("offerType", form);

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-100 shadow-sm mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Discount Configuration
      </h3>

      {offerType === "Coupon" && (
        <div className="flex items-center gap-4">
          <Form.Item
            label="Campaign Code"
            name="code"
            rules={[{ required: true, message: "Please enter campaign code" }]}
            className="w-full"
          >
            <Input placeholder="e.g., SALE2024" disabled={disabled} />
          </Form.Item>

          <Button
            type="default"
            size="large"
            onClick={onGenerateCode}
            loading={isGenerating}
            className="text-theme-primary-500 font-medium hover:text-theme-primary-600 mt-3"
          >
            Generate
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Form.Item
          label="Discount Type"
          name="discountType"
          rules={[{ required: true, message: "Please select discount type" }]}
        >
          <Select
            placeholder="Select"
            disabled={disabled}
            options={[
              { label: "Amount", value: "amount" },
              { label: "Percentage", value: "percentage" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Discount Value"
          name="discountValue"
          dependencies={["discountType"]}
          rules={[
            { required: true, message: "Please enter discount value" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const type = getFieldValue("discountType");

                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }

                if (value < 0) {
                  return Promise.reject(
                    new Error("Discount value cannot be negative"),
                  );
                }

                if (type === "percentage" && value > 100) {
                  return Promise.reject(
                    new Error("Percentage cannot be more than 100"),
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter value"
            disabled={disabled}
            // min={0}
            // max={discountType === "percentage" ? 100 : undefined}
          />
        </Form.Item>

        <Form.Item
          label="Per User Limit"
          name="perUserLimit"
          rules={[
            { required: true, message: "Please enter per user limit" },
            {
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                if (value < 0) {
                  return Promise.reject(new Error("Value cannot be negative"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="e.g., 5000"
            disabled={disabled}
            min={0}
          />
        </Form.Item>

        <Form.Item
          label="Maximum Budget (৳)"
          name="maxBudget"
          rules={[
            { required: true, message: "Please enter maximum budget" },
            {
              validator(_, value) {
                if (value === null || value === undefined || value === "") {
                  return Promise.resolve();
                }
                if (value < 0) {
                  return Promise.reject(new Error("Value cannot be negative"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="e.g., 50000"
            disabled={disabled}
            min={0}
          />
        </Form.Item>
      </div>

      {/* {offerType === "Campaign" && ( */}
      <Form.Item
        label="Hide From UI"
        name="isHidden"
        valuePropName="checked"
        tooltip="When enabled, this campaign will be hidden from the user interface, Discount apply only checkout page."
        className="mt-2"
      >
        <Checkbox disabled={disabled}>Hide this campaign from users</Checkbox>
      </Form.Item>
      {/* )} */}

      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Courses</h4>
        <Form.Item name="courses">
          <Checkbox.Group
            className="w-full flex flex-col gap-2"
            disabled={disabled}
          >
            {courseOptions.map((course) => (
              <Checkbox
                key={course.value}
                value={course.value}
                className="w-full border border-gray-200 rounded p-3 ml-0 hover:bg-gray-50 transition-colors"
                disabled={disabled}
              >
                {course.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>
      </div>
    </div>
  );
};

export default DiscountConfiguration;
